import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, Facebook } from 'lucide-react';

const services = [
  { name: 'SEO & Organic Growth', slug: 'seo-organic-growth' },
  { name: 'Performance Marketing', slug: 'performance-marketing' },
  { name: 'Website Design & Development', slug: 'website-design-development' },
  { name: 'Social Media Marketing', slug: 'social-media-marketing' },
  { name: 'Conversion Rate Optimization', slug: 'conversion-rate-optimization' },
  { name: 'Branding & Strategy', slug: 'branding-strategy' },
];

const industries = [
  { name: 'Healthcare & Aesthetics', slug: 'healthcare-aesthetics' },
  { name: 'Education & Coaching', slug: 'education-coaching' },
  { name: 'Real Estate', slug: 'real-estate' },
  { name: 'Professional Services', slug: 'professional-services' },
  { name: 'Local Businesses', slug: 'local-businesses' },
  { name: 'Startups & SMEs', slug: 'startups-smes' },
];

// SEO Location Keywords - Primary targeting locations
const seoLocations = {
  india: [
    { name: 'SEO Services Mumbai', slug: 'website-seo-mumbai' },
    { name: 'GMB SEO Mumbai', slug: 'gmb-seo-mumbai' },
    { name: 'SEO Services Pune', slug: 'website-seo-pune' },
    { name: 'SMM Agency Mumbai', slug: 'social-media-marketing-mumbai' },
    { name: 'PPC Agency Pune', slug: 'performance-marketing-pune' },
  ],
  international: [
    { name: 'SEO Services Singapore', slug: 'website-seo-singapore' },
    { name: 'SEO Agency USA', slug: 'website-seo-usa' },
    { name: 'SEO Services Australia', slug: 'website-seo-australia' },
    { name: 'GMB SEO Singapore', slug: 'gmb-seo-singapore' },
    { name: 'PPC Agency USA', slug: 'performance-marketing-usa' },
  ]
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#2E2E2E] text-white" data-testid="main-footer">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo-icon.png" 
                alt="Digital Startup" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold">
                Digital <span className="text-[#FFAA17]">Startup</span>
              </span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Leading digital marketing agency delivering ROI-focused SEO, Performance Marketing, 
              and Social Media Marketing services across Mumbai, Pune, Singapore, USA & Australia.
            </p>
            <div className="flex gap-3">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FFAA17] transition-colors"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FFAA17] transition-colors"
                aria-label="Twitter"
                data-testid="footer-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FFAA17] transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FFAA17] transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-gray-400 hover:text-[#FFAA17] transition-colors"
                    data-testid={`footer-service-${service.slug}`}
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Industries</h4>
            <ul className="space-y-3">
              {industries.map((industry) => (
                <li key={industry.slug}>
                  <Link
                    to={`/industries/${industry.slug}`}
                    className="text-gray-400 hover:text-[#FFAA17] transition-colors"
                    data-testid={`footer-industry-${industry.slug}`}
                  >
                    {industry.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-[#FFAA17] flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:hello@digitalstartup.in"
                  className="text-gray-400 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  hello@digitalstartup.in
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-[#FFAA17] flex-shrink-0 mt-0.5" />
                <a
                  href="tel:+919867958759"
                  className="text-gray-400 hover:text-white transition-colors"
                  data-testid="footer-phone"
                >
                  +91 98679 58759
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#FFAA17] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">
                  Mumbai, India
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO Keywords Section */}
        <div className="border-t border-white/10 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* India Services */}
            <div>
              <h5 className="text-sm font-semibold text-[#FFAA17] mb-4 uppercase tracking-wide">
                India
              </h5>
              <ul className="space-y-2">
                {seoLocations.india.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/services/${item.slug}`}
                      className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* International Services */}
            <div>
              <h5 className="text-sm font-semibold text-[#FFAA17] mb-4 uppercase tracking-wide">
                International
              </h5>
              <ul className="space-y-2">
                {seoLocations.international.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/services/${item.slug}`}
                      className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Service Links */}
            <div>
              <h5 className="text-sm font-semibold text-[#FFAA17] mb-4 uppercase tracking-wide">
                Popular Services
              </h5>
              <ul className="space-y-2">
                <li>
                  <Link to="/services/gmb-seo-mumbai" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Google My Business SEO
                  </Link>
                </li>
                <li>
                  <Link to="/services/website-seo-mumbai" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Website SEO Services
                  </Link>
                </li>
                <li>
                  <Link to="/services/social-media-marketing-mumbai" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Social Media Marketing
                  </Link>
                </li>
                <li>
                  <Link to="/services/performance-marketing-mumbai" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Performance Marketing
                  </Link>
                </li>
                <li>
                  <Link to="/insights" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Digital Marketing Blog
                  </Link>
                </li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h5 className="text-sm font-semibold text-[#FFAA17] mb-4 uppercase tracking-wide">
                Resources
              </h5>
              <ul className="space-y-2">
                <li>
                  <Link to="/work" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Case Studies
                  </Link>
                </li>
                <li>
                  <Link to="/insights" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Marketing Insights
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-500 hover:text-[#FFAA17] transition-colors">
                    Free Growth Audit
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* SEO Keywords Bar */}
        <div className="border-t border-white/10 py-6">
          <div className="flex flex-wrap gap-x-4 gap-y-2 justify-center text-xs text-gray-600">
            <Link to="/services/website-seo-mumbai" className="hover:text-gray-400">SEO Company Mumbai</Link>
            <span>•</span>
            <Link to="/services/gmb-seo-mumbai" className="hover:text-gray-400">Local SEO Mumbai</Link>
            <span>•</span>
            <Link to="/services/website-seo-pune" className="hover:text-gray-400">SEO Agency Pune</Link>
            <span>•</span>
            <Link to="/services/performance-marketing-mumbai" className="hover:text-gray-400">PPC Management Mumbai</Link>
            <span>•</span>
            <Link to="/services/social-media-marketing-mumbai" className="hover:text-gray-400">Social Media Agency Mumbai</Link>
            <span>•</span>
            <Link to="/services/website-seo-singapore" className="hover:text-gray-400">SEO Singapore</Link>
            <span>•</span>
            <Link to="/services/website-seo-usa" className="hover:text-gray-400">SEO Services USA</Link>
            <span>•</span>
            <Link to="/services/website-seo-australia" className="hover:text-gray-400">SEO Australia</Link>
            <span>•</span>
            <Link to="/services/gmb-seo-singapore" className="hover:text-gray-400">GMB SEO Singapore</Link>
            <span>•</span>
            <Link to="/services/performance-marketing-usa" className="hover:text-gray-400">Google Ads USA</Link>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="border-t border-white/10 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {currentYear} Digital Startup. All rights reserved. | Digital Marketing Agency Mumbai, Pune, Singapore, USA, Australia
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
